/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectDisableOptions, PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';
import { FormGroup } from '@angular/forms';
import { CustomersService } from 'app/core/services/customers/customers.service';

@Component({
  selector: 'property-data-picker',
  templateUrl: './property-data-picker.component.html',
  styleUrls: ['./property-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedProperties: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() options: any = null;
  @Input() propertyUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'created_at';
  @Input() sortType: string = 'DESC';
  @Input() customParams: any = null;
  @Input() selectionDisable: { selectionDisableBasedOn: PickerSelectDisableOptions; elementId?: string } = null; //NA for readonly selectOption
  @Input() filterFormGroup: FormGroup;
  @Input() hasFilterValue: boolean = false;
  @Input() propertyData: any[] = null;

  duplicatedPropertyData: any[] = [];
  properties: any[] = [];
  propertiesList: any[] = [];
  propertyEleId: string = '';
  propertyDetails: any = null;
  loaded: boolean = false;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  isSpinLoading: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private customerService: CustomersService,
    private toast: HotToastService) {
  }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  rotateArrowIcon(propertyUID: any): any {
    return {
      'rotate-arrow-icon-down': this.isExpandingAction(propertyUID),
      'rotate-arrow-icon-up': !this.isExpandingAction(propertyUID)
    };
  }

  showSpinLoading(propertyUID: any): boolean {
    return this.isExpandingAction(propertyUID) && this.isSpinLoading;
  }

  updateArrowTooltipText(propertyUID: any): string {
    return this.isExpandingAction(propertyUID) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(propertyUID: any): boolean {
    return (this.propertyEleId === 'property_details_' + propertyUID);
  }

  viewPropertyFullDetails(propertyUID: string): string {
    return '/property/' + propertyUID + '/details';
  }

  getStatusBadgeOptions(type: string, displayName: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: type,
      displayType: type,
      displayKey: null,
      displayName: displayName ?? type
    };
    return badgeOption;
  }

  viewPropertyDetails(event: any, propertyUID: string): void {
    event.stopPropagation();
    const elementId: string = 'property_details_' + propertyUID;
    if (this.propertyEleId === elementId) {
      this.resetPropertyDetails();
    } else {
      this.propertyEleId = elementId;
      this.getPropertyDetails(propertyUID);
    }
  }

  getPropertyDetails(propertyUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.propertyUrl + `/` + propertyUID).subscribe(
      /* eslint-enable quotes */
      (properties: any) => {
        this.propertyDetails = properties.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.resetPropertyDetails();
        this.isSpinLoading = false;
      });
  }

  resetPropertyDetails(): void {
    this.propertyEleId = '';
    this.propertyDetails = null;
  }

  isSelectedProperty(property: any): boolean {
    if (this.selectedProperties?.length) {
      return this.selectedProperties.find(value => value.property_uid === property.property_uid) ? true : false;
    }
    return false;
  }

  selectProperty(event: any, property: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedProperties.push(property);
      } else {
        this.selectedProperties = this.selectedProperties.filter(value => value.property_uid !== property.property_uid);
      }
    } else {
      this.selectedProperties = [property];
    }
    this.eventEmitter.emit({ value: this.selectedProperties });
  }

  triggerSearch(searchKey: any): void {
    this.onSearch = true;
    if (this.propertyData) {
      this.propertyDataSearch(searchKey);
    } else {
      this.propertyRemoteDataSearch(searchKey);
    }
  }

  propertyDataSearch(searchKey: any): void {
    if (!searchKey) {
      this.properties = this.duplicatedPropertyData;
    } else {
      const filteredValue: any[] = this.duplicatedPropertyData?.filter((property: any) => {
        searchKey = searchKey.toLowerCase();
        const propertyName: string = property?.property_name?.toLowerCase();
        if (propertyName?.indexOf(searchKey) > -1) {
          return property;
        }
      });
      this.properties = filteredValue?.length ? [...filteredValue] : [];
    }
  }

  propertyRemoteDataSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex??0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.propertyEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    if (this.defaultParamObj['filter.organization'] || this.defaultParamObj['filter.customer']) {
      delete this.defaultParamObj['filter.has_org'];
    } else if (this.options?.filters?.getPropertyWithoutOrg || (this.options?.selectionDisableBasedOn === 'CUSTOMER' &&
        this.utilityService.isEmptyOrUndefined(this.customerService.selectedOrganizationPropertyPickerFilterValue.value))) {
      this.defaultParamObj['filter.has_org'] = false;
    }
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.propertyUrl, urlParams).subscribe(
      (response: any) => {
        this.propertiesList = response.data;
        this.properties = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.properties.length) {
          this.selectedProperties = this.selectedProperties?.length > 0 ? _.uniqBy([...this.selectedProperties, ...this.filter], 'property_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedProperties });
        } else if (isOpen && this.properties.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex??0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (!this.propertyData && this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].property_uid;
    }
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues.moduleBasedFilterValue) {
      let hasModuleFilterValue: boolean = false;
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
          hasModuleFilterValue = true;
        } else {
          delete params[value?.key];
        }
      });
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  updatePropertyData(): void {
    this.properties = this.propertyData;
    this.duplicatedPropertyData = [...this.propertyData];
    this.onSearch = false;
    this.loaded = true;
    if (this.filter && this.propertyData?.length) {
      this.selectedProperties = this.filter;
      this.eventEmitter.emit({ value: this.selectedProperties });
    }
    if (this.searchKey) {
      this.eventEmitter.emit({ searchKey: this.searchKey });
      this.triggerSearch(this.searchKey);
    }
    this.changeDetectorRef.detectChanges();
  }

  resetPropertyDataPicker(): void {
    this.properties = [];
    this.changeDetectorRef.detectChanges();
  }

  showDataPickerPanel(): void {
    this.resetPropertyDataPicker();
    if (this.propertyData) {
      this.updatePropertyData();
    } else if (this.showSelectedItems) {
      this.properties = this.selectedProperties;
    } else if (this.propertiesList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.propertiesList.length > 0 || this.searchKey) {
      this.properties = this.propertiesList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedProperties, filter: this.selectedProperties });
  }

  validateSelectionDisable(property: any): boolean {
    let propertyAssociatedToCustomer: boolean = false;
    if (property?.property_customers?.length > 0 && this.selectionDisable?.selectionDisableBasedOn === PickerSelectDisableOptions.CUSTOMER) {
      property?.property_customers.forEach((item) => {
        if (item.customer.customer_uid === this.selectionDisable?.elementId) {
          propertyAssociatedToCustomer = true;
        }
      });
    }
    return (this.selectionDisable?.selectionDisableBasedOn === PickerSelectDisableOptions.PROPERTY && property?.property_uid === this.selectionDisable?.elementId
      || propertyAssociatedToCustomer
    );
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedProperties ? this.selectedProperties.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false || changes?.propertyData?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }

  isValidValue(value: any): boolean {
    return value !== undefined && value !== null;
  }

  getDisableMessage(property: any): string {
    if (this.validateSelectionDisable(property)) {
      switch (this.selectionDisable?.selectionDisableBasedOn) {
        case PickerSelectDisableOptions.CUSTOMER:
          return this.utilityService.getLocaleValue('dataPicker.property.selection_already_associated.text');
        case PickerSelectDisableOptions.PROPERTY:
          return this.utilityService.getLocaleValue('dataPicker.property.selection_disable.text');
      }
    }
  }
}
